'use client';
import { useEffect, useRef, useState } from 'react';
import { useTranslations } from 'next-intl';
import PlusIcon from '@/components/icons/PlusIcon';
import MinusIcon from '@/components/ui/icons/minus-icon';
import AddToCart from '@/components/ui/buttons/add-to-cart';
import BuyNow from '@/components/ui/buttons/buy-now-button';
import { getCookie } from 'cookies-next';
import useHandleCart from '@/hooks/cart/useHandleCart';
import useCart from '@/store/cart.store';
import RedirectToCheckout from '@/components/global/RedirectToCheckout';

const CtaButtons = ({ product, selectedBundle }) => {
  const [added, setAdded] = useState(false);
  const t = useTranslations('product');
  const pdpRef = useRef();
  const isMobile = getCookie('isMobile') === 'true';

  const cartItems = useCart((state) => state.cartItems);

  const singleProduct = cartItems?.find(
    (item) =>
      item?.innova_url ||
      item?.extension_attributes?.innova_url === product?.innova_url
  );

  const [quantity, setQuantity] = useState(1);

  const { handleAddToCart, loading, handleUpdateCart } = useHandleCart();

  const isAdding = loading === 'adding';
  const isSubtracting = loading === 'subtracting';

  useEffect(() => {
    isMobile ? pdpRef.current?.click() : null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quantity]);

  useEffect(() => {
    setQuantity(singleProduct?.qty || 1);
  }, [singleProduct?.qty]);

  return (
    <div className='flex flex-col gap-2 lg:gap-4'>
      <div className='flex flex-col gap-4 border-b border-gray-200 max-md:fixed max-md:start-0 max-md:right-0 max-md:bottom-[59px] max-md:left-0 max-md:z-24 max-md:max-h-16.5 max-md:min-h-16.5 max-md:w-full max-md:flex-row max-md:justify-between max-md:bg-white max-md:px-7 max-md:py-2 max-md:shadow-[0_-4px_16px_rgba(2,6,23,0.08)]'>
        <div
          className={`flex w-full items-center justify-between ${!added && isMobile ? 'max-md:hidden' : ''}`}
        >
          <h4 className='text-sm text-[#333] max-md:hidden'>{t('Quantity')}</h4>

          {/*  qty controller desktop */}
          <div
            className={`hidden items-center rounded-lg border border-gray-300 bg-white shadow-sm md:flex`}
          >
            <button
              disabled={quantity === 1}
              onClick={() => setQuantity(quantity - 1)}
              aria-label={t('decrease')}
              className='px-3 py-2 text-gray-600 hover:bg-gray-50 disabled:cursor-not-allowed'
            >
              <MinusIcon className='size-3.5' />
            </button>
            <span className='px-4 py-2 font-medium text-gray-800'>
              {quantity}
            </span>
            <button
              aria-label={t('increase')}
              onClick={() => setQuantity(quantity + 1)}
              className='px-3 py-2 text-gray-600 hover:bg-gray-50'
            >
              <PlusIcon className='size-3.5' />
            </button>
          </div>

          {/*  qty controller mobile */}
          <div className='flex w-full items-center justify-between md:hidden'>
            <div className=''>
              {singleProduct && (
                <div
                  className={`flex items-center rounded-lg border border-gray-300 bg-white shadow-sm`}
                >
                  <button
                    onClick={() =>
                      handleUpdateCart(
                        true,
                        singleProduct?.item_id,
                        singleProduct?.qty,
                        product
                      )
                    }
                    disabled={
                      isAdding || isSubtracting || singleProduct?.qty <= 1
                    }
                    aria-label={t('decrease')}
                    className='px-3 py-2 text-gray-600 hover:bg-gray-50 disabled:cursor-not-allowed'
                  >
                    {isSubtracting ? (
                      <div className='size-4 animate-spin rounded-full border-t border-r border-[#aaa] py-1' />
                    ) : (
                      <MinusIcon className='size-3.5' />
                    )}
                  </button>
                  <span className='px-4 py-2 font-medium text-gray-800'>
                    {quantity}
                  </span>
                  <button
                    aria-label={t('increase')}
                    className='px-3 py-2 text-gray-600 hover:bg-gray-50'
                    onClick={() => handleAddToCart(product, 1, t)}
                    disabled={isAdding || isSubtracting}
                  >
                    {isAdding ? (
                      <div className='size-4 animate-spin rounded-full border-t border-r border-[#aaa] py-1' />
                    ) : (
                      <PlusIcon className='size-3.5' />
                    )}
                  </button>
                </div>
              )}
            </div>

            <div className='flex flex-1 items-center justify-end gap-2'>
              {!singleProduct && (
                <AddToCart
                  product={product}
                  qty={Number(selectedBundle) || quantity}
                  fromPdp
                  fromNewPdp
                  added={added}
                  setAdded={getCookie('isMobile') === 'true' ? setAdded : false}
                  pdpRef={pdpRef}
                />
              )}

              {singleProduct && <RedirectToCheckout type={'fromCtaButtons'} />}
            </div>
          </div>
        </div>
        <AddToCart
          product={product}
          qty={Number(selectedBundle) || quantity}
          fromPdp
          added={added}
          setAdded={getCookie('isMobile') === 'true' ? setAdded : false}
          pdpRef={pdpRef}
        />
      </div>
      <BuyNow product={product} qty={Number(selectedBundle) || quantity} />
    </div>
  );
};

export default CtaButtons;
